import path from "node:path";
import type { ThinkFrameworkAgent, ThinkFrameworkManifest } from "./manifest";

export const THINK_TYPES_BEGIN = "// <think-generated>";
export const THINK_TYPES_END = "// </think-generated>";

const GENERATED_FILE_HEADER = [
  "// Generated by `think types`. Do not edit this file directly.",
  "// Re-run `think types` after changing Think agents or Wrangler bindings."
].join("\n");

export interface ThinkTypesCodegenOptions {
  files?: Record<string, string>;
  typesFile?: string;
}

export interface ThinkGeneratedFile {
  path: string;
  content: string;
}

export function generateThinkTypes(
  manifest: ThinkFrameworkManifest,
  options: ThinkTypesCodegenOptions = {}
): ThinkGeneratedFile[] {
  const typesFile = normalizePath(options.typesFile ?? "think.d.ts");
  return [
    {
      path: typesFile,
      content: generateThinkEnvBlock(manifest, {
        ...options,
        typesFile
      })
    }
  ];
}

export function isThinkGeneratedFile(source: string): boolean {
  return source.startsWith(GENERATED_FILE_HEADER);
}

function generateThinkEnvBlock(
  manifest: ThinkFrameworkManifest,
  options: Required<Pick<ThinkTypesCodegenOptions, "typesFile">> &
    ThinkTypesCodegenOptions
): string {
  const sourceFile = options.typesFile;
  const entryExports = manifest.agents.map((agent) => {
    return `  export const ${agent.className}: ${agentConstructorType(agent, sourceFile, options.files)};`;
  });
  const bindingExports = topLevelAgents(manifest).map((agent) => {
    const binding = agent.bindingName ?? agent.className;
    return [
      `    ${binding}: DurableObjectNamespace<`,
      `      ${agentInstanceType(agent, sourceFile, options.files)}`,
      `    >;`
    ].join("\n");
  });

  return (
    [
      GENERATED_FILE_HEADER,
      "",
      THINK_TYPES_BEGIN,
      `declare module "virtual:think/entry" {`,
      `  const entry: ExportedHandler<Cloudflare.Env>;`,
      `  export default entry;`,
      ...entryExports,
      `}`,
      ``,
      `declare module "virtual:think/router" {`,
      `  export const thinkRouter: import("@cloudflare/think/server-entry").ThinkRouter;`,
      `}`,
      bindingExports.length
        ? [
            ``,
            `declare namespace Cloudflare {`,
            `  interface Env {`,
            ...bindingExports,
            `  }`,
            `}`
          ].join("\n")
        : "",
      THINK_TYPES_END,
      ""
    ]
      .filter((line) => line.length > 0)
      .join("\n") + "\n"
  );
}

function agentConstructorType(
  agent: ThinkFrameworkAgent,
  fromFile: string,
  files: Record<string, string> | undefined
): string {
  const exportInfo = inferAgentExport(agent, files);
  if (exportInfo.kind === "class") {
    const specifier = relativeModuleSpecifier(fromFile, agent.sourcePath);
    return `(typeof import(${JSON.stringify(specifier)}))${exportAccessor(exportInfo.exportName)}`;
  }
  return `typeof import("@cloudflare/think").Think<Cloudflare.Env>`;
}

function agentInstanceType(
  agent: ThinkFrameworkAgent,
  fromFile: string,
  files: Record<string, string> | undefined
): string {
  const exportInfo = inferAgentExport(agent, files);
  if (exportInfo.kind === "class") {
    const specifier = relativeModuleSpecifier(fromFile, agent.sourcePath);
    return `InstanceType<(typeof import(${JSON.stringify(specifier)}))${exportAccessor(exportInfo.exportName)}>`;
  }
  return `import("@cloudflare/think").Think<Cloudflare.Env>`;
}

function inferAgentExport(
  agent: ThinkFrameworkAgent,
  files: Record<string, string> | undefined
): { kind: "class"; exportName: string } | { kind: "declarative" } {
  const source = files?.[agent.sourcePath];
  if (!source) {
    return agent.exportName
      ? { kind: "class", exportName: agent.exportName }
      : { kind: "declarative" };
  }

  if (/export\s+default\s+agent\s*\(/.test(source)) {
    return { kind: "declarative" };
  }
  if (/export\s+default\s+class\b/.test(source)) {
    return { kind: "class", exportName: "default" };
  }
  const namedClass = source.match(
    /export\s+(?:abstract\s+)?class\s+([A-Za-z_$][\w$]*)/
  );
  if (namedClass?.[1]) {
    return { kind: "class", exportName: namedClass[1] };
  }
  const namedDeclarative = source.match(
    /export\s+const\s+([A-Za-z_$][\w$]*)\s*=\s*agent\s*\(/
  );
  if (namedDeclarative) {
    return { kind: "declarative" };
  }
  const reexportDefault = source.match(
    /export\s*\{\s*([A-Za-z_$][\w$]*)\s+as\s+default\s*\}/
  );
  if (reexportDefault?.[1]) {
    return { kind: "class", exportName: "default" };
  }

  return agent.exportName
    ? { kind: "class", exportName: agent.exportName }
    : { kind: "declarative" };
}

function exportAccessor(exportName: string): string {
  return exportName === "default"
    ? ".default"
    : `[${JSON.stringify(exportName)}]`;
}

function topLevelAgents(
  manifest: ThinkFrameworkManifest
): ThinkFrameworkAgent[] {
  return manifest.agents.filter((agent) => agent.kind === "top-level");
}

function relativeModuleSpecifier(fromFile: string, targetFile: string): string {
  const fromDirectory = dirname(fromFile);
  const withoutExtension = stripExtension(targetFile);
  let relative = path.posix.relative(fromDirectory, withoutExtension);
  if (!relative.startsWith(".")) relative = `./${relative}`;
  return relative;
}

function stripExtension(file: string): string {
  return file.replace(/\.(?:d\.)?(?:ts|tsx|js|mjs)$/, "");
}

function dirname(file: string): string {
  const normalized = normalizePath(file);
  const directory = path.posix.dirname(normalized);
  return directory === "." ? "." : directory;
}

function normalizePath(file: string): string {
  return file.replace(/\\/g, "/").replace(/^\.?\//, "");
}
